import { jwtDecode } from 'jwt-decode';

import { logger } from '@client/utils/logger.util';
import { EUserRole, type TUserRole } from '@common/types';

export const isAdmin = (role: TUserRole): boolean => role === EUserRole.ADMIN;

export const isEditor = (role: TUserRole): boolean => role === EUserRole.EDITOR;

export const isViewer = (role: TUserRole): boolean => role === EUserRole.VIEWER;

export const isConsumer = (role: TUserRole): boolean =>
  role === EUserRole.CONSUMER;

// Additional authentication utilities for JWT token handling
export interface DecodedToken {
  sub: string;
  email: string;
  exp: number;
  iat?: number;
  role?: string;
  permissions?: string[];
}

export interface User {
  uid: string;
  email: string;
  displayName?: string;
  customClaims?: {
    role?: string;
    permissions?: string[];
  };
}

/**
 * Checks if a JWT token is valid and not expired
 */
export function isTokenValid(token: string | null | undefined): boolean {
  if (!token || token.trim() === '') {
    return false;
  }

  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp > currentTime;
  } catch (error) {
    logger.error('Error decoding token:', error);
    return false;
  }
}

/**
 * Decodes a JWT token and returns the payload
 */
export function getTokenPayload(
  token: string | null | undefined,
): DecodedToken | null {
  if (!token || token.trim() === '') {
    return null;
  }

  try {
    return jwtDecode<DecodedToken>(token);
  } catch (error) {
    logger.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Extracts user ID from a JWT token
 */
export function getUserIdFromToken(
  token: string | null | undefined,
): string | null {
  const payload = getTokenPayload(token);
  return payload?.sub || null;
}

/**
 * Checks if a user has an admin role
 */
export function isUserAdmin(user: User | null | undefined): boolean {
  if (!user) {
    return false;
  }

  return user.customClaims?.role === 'admin';
}

/**
 * Checks if a user has specific permission
 */
export function userHasPermission(
  user: User | null | undefined,
  permission: string,
): boolean {
  if (!user || !user.customClaims?.permissions) {
    return false;
  }

  return user.customClaims.permissions.includes(permission);
}

/**
 * Formats user display name
 */
export function getUserDisplayName(user: User | null | undefined): string {
  if (!user) {
    return 'Anonymous';
  }

  return user.displayName || user.email || 'User';
}

/**
 * Validates email format
 */
export function isValidEmail(email: string): boolean {
  if (!email || email.trim() === '') {
    return false;
  }

  const trimmedEmail = email.trim();

  // Check for basic format issues
  if (
    trimmedEmail.includes('..') ||
    trimmedEmail.includes(' ') ||
    trimmedEmail.startsWith('.') ||
    trimmedEmail.endsWith('.') ||
    trimmedEmail.startsWith('@') ||
    trimmedEmail.endsWith('@')
  ) {
    return false;
  }

  // Must contain exactly one @ symbol
  const atCount = (trimmedEmail.match(/@/g) || []).length;
  if (atCount !== 1) {
    return false;
  }

  const [localPart, domainPart] = trimmedEmail.split('@');

  // Local part validation
  if (!localPart || localPart.length === 0 || localPart.length > 64) {
    return false;
  }

  // Domain part validation
  if (!domainPart || domainPart.length === 0 || domainPart.length > 253) {
    return false;
  }

  // Domain must contain at least one dot and have a valid TLD
  if (!domainPart.includes('.')) {
    return false;
  }

  // Check domain parts
  const domainParts = domainPart.split('.');
  if (domainParts.length < 2) {
    return false;
  }

  // Validate each domain part
  for (const part of domainParts) {
    if (
      !part ||
      part.length === 0 ||
      part.startsWith('-') ||
      part.endsWith('-')
    ) {
      return false;
    }
    // Each part should contain only valid characters
    if (!/^[a-zA-Z0-9-]+$/.test(part)) {
      return false;
    }
  }

  // Final regex check for overall format
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

  return emailRegex.test(trimmedEmail);
}

/**
 * Validates password strength
 */
export function isValidPassword(password: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
